import { useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { DocumentFilterItems } from "src/pages/App/pages/Organization/pages/Document/services/types";
import { useWindowSize } from "go-core/components/useWindowSize";

interface Props<T extends DocumentFilterItems> {
	numberOfColumns: number;
	items: T[];
	numberOfFilteredItems: number;
	setItems: React.Dispatch<React.SetStateAction<T[]>>;
}

const convertPolishChars = (strings: string[]): string[] =>
	strings.map((string) =>
		string
			.normalize("NFKD")
			.replace(/[\u0300-\u036f]/g, "")
			.replaceAll(/[ł]/gi, "l")
	);

const getFilteredResult = (itemName: string, filterValue: string): boolean => {
	const [convertedName, convertedFilterValue] = convertPolishChars([itemName, filterValue]);

	return convertedName.toLowerCase().includes(convertedFilterValue.toLowerCase());
};

export const DocumentItemsFilter = <T extends DocumentFilterItems>({
	numberOfColumns,
	items,
	numberOfFilteredItems,
	setItems,
}: Props<T>) => {
	const [filterValue, setFilterValue] = useState("");
	const { t } = useTranslation();
	const { isMobile } = useWindowSize();

	const filterItems = () => {
		const newFilteredItems = items.filter((item) => {
			if ("product" in item && item.product) {
				return getFilteredResult(item.product.name, filterValue);
			} else if ("name" in item && item.name) {
				return getFilteredResult(item.name, filterValue);
			}

			return false;
		});

		setItems(newFilteredItems);
	};

	const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => setFilterValue(event.target.value);

	const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.code === "Enter" || event.code === "NumpadEnter") {
			filterItems();
		}
	};

	return (
		<>
			<tr>
				<td colSpan={numberOfColumns} className="w-100">
					<Form.Control
						type="search"
						value={filterValue}
						placeholder={t("common.word.search", { ns: "lib" })}
						onChange={changeHandler}
						onKeyDown={keyDownHandler}
						onBlur={filterItems}
						className={`${isMobile ? "w-50" : "w-40"} ms-1`}
					/>
				</td>
			</tr>
			{numberOfFilteredItems === 0 && filterValue && (
				<tr>
					<td colSpan={numberOfColumns} className="p-0 ps-1 text-start">
						{t("modules.document.field.empty_data.title")}
					</td>
				</tr>
			)}
		</>
	);
};
